import { useCallback, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/integrations/firebase/firestore";
import { logError } from "@/shared/services/ErrorReporting";

export interface UserSearchResult {
  uid: string;
  email: string;
}

const MAX_RESULTS = 10;

export type SearchUsersResult =
  { ok: true; users: UserSearchResult[] } | { ok: false };

export function useSearchUsersByEmail() {
  const [isSearching, setIsSearching] = useState(false);

  const searchUsers = useCallback(
    async (rawPrefix: string): Promise<SearchUsersResult> => {
      const prefix = rawPrefix.trim().toLowerCase();
      if (prefix.length === 0) {
        return { ok: true, users: [] };
      }

      setIsSearching(true);
      try {
        const usersRef = collection(db, "users");
        const q = query(
          usersRef,
          where("email", ">=", prefix),
          where("email", "<", prefix + "\uf8ff"),
          orderBy("email"),
          limit(MAX_RESULTS),
        );

        const snapshot = await getDocs(q);
        const users: UserSearchResult[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (typeof data.uid === "string" && typeof data.email === "string") {
            users.push({ uid: data.uid, email: data.email });
          }
        });

        return { ok: true, users };
      } catch (err) {
        logError(err as Error);
        return { ok: false };
      } finally {
        setIsSearching(false);
      }
    },
    [],
  );

  return { searchUsers, isSearching };
}
